import React, { useState, useEffect } from 'react';
import { Settings, Save, RotateCcw, DollarSign, Lock, Unlock, Percent, History, Search, Trash2, Download, BookOpen, FileText, Handshake, LogOut, Loader2, Check, CreditCard } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_SETTINGS = {
  words_per_hour: 9005,
  pages_per_hour: 30,
  usd_exchange_rate: 17.5,
  show_audiobook_section: true,
  show_podcast_section: false,
  show_servicios_section: true,
  show_meditaciones_section: false,
  show_audiocursos_section: false,
  show_viubux_connect_section: false,
  show_editorial_section: false,
  show_bank_transfer: false,
  show_walking_podcast_section: false,
  show_renta_equipo_section: false,
  show_transmision_section: false,
  show_sonido_directo_section: false,
  show_foro_section: false,
  show_interpretacion_section: false,
  show_sonorizacion_section: false,
  show_voice_services_section: true,
  show_studio_fees_section: true,
  show_hand_free_section: true,
  show_ultra_package: true,
  // Co-Producción
  show_coproduccion: false,
  coproduccion_percent: 50,
  // Service prices - Distribución
  price_dist_essential: 697,
  price_dist_platino: 1697,
  price_dist_premium: 2997,
  // Service prices - Booktrailer
  price_bt_essential: 697,
  price_bt_platino: 4997,
  price_bt_premium: 8997,
  // Service prices - Kit Legal
  price_legal_registro: 2500,
  price_legal_isbn: 2000,
  price_legal_combo: 4000,
  // Service prices - Diseño de Portada
  price_cover_design: 1250,
  // Service prices - Pack Branding
  price_branding_essential: 2500,
  price_branding_pro: 4500,
  price_branding_premium: 7500,
  // Service prices - Música y Consultoría
  price_music_stock: 797,
  price_consultoria: 850,
  // New additional services
  price_qr_dinamico: 397,
  price_enlace_go: 197,
  price_nfc_vcard: 1997,
  price_separadores: 397,
  price_preventa: 897,
  price_banner_promotion: 197,
  price_diseno_web_banner: 997,
  price_photoshoot: 2500,
  price_pagina_autor: 3500,
  // Service prices - Podcast Paquetes
  price_pod_essential: 997,
  price_pod_platino: 3500,
  price_pod_premium: 5500,
  // Service prices - Podcast Addons
  price_pod_set_basico: 850,
  price_pod_set_virtual: 350,
  // Packages
  packages: [
    { id: 'ultra', name: 'Ultra', base_price: 3499 },
    { id: 'essential', name: 'Essential', base_price: 6997 },
    { id: 'platino', name: 'Platino', base_price: 13997 },
    { id: 'premium', name: 'Premium', base_price: 21997 }
  ]
};

export function SettingsModal({ settings, onSave, isOpen, onOpenChange, showDiscounts, onToggleDiscounts, customDiscount, onCustomDiscountChange, customDiscountType, onCustomDiscountTypeChange, onLoadQuote }) {
  const [localSettings, setLocalSettings] = useState(settings);
  const [isSaving, setIsSaving] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('vbx_admin_auth') === 'true';
  });
  const [password, setPassword] = useState(() => {
    return localStorage.getItem('vbx_admin_pwd') || '';
  });
  const [isVerifying, setIsVerifying] = useState(false);
  const [localCustomDiscount, setLocalCustomDiscount] = useState(customDiscount || 0);
  
  // History state
  const [quotes, setQuotes] = useState([]);
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Materials state
  const [materials, setMaterials] = useState([]);
  const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);

  // Invoices (Facturación) state
  const [invoices, setInvoices] = useState([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [blikonCredits, setBlikonCredits] = useState(null);
  const [allowedUsers, setAllowedUsers] = useState([]);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  // Load allowed users when authenticated
  useEffect(() => {
    if (isAuthenticated && password) {
      axios.get(`${API}/auth/users?password=${password}`)
        .then(r => setAllowedUsers(r.data))
        .catch(() => {});
    }
  }, [isAuthenticated, password]);

  useEffect(() => {
    setLocalCustomDiscount(customDiscount || 0);
  }, [customDiscount]);

  useEffect(() => {
    if (!isOpen) {
      // Keep auth state persisted - only reset transient UI state
      setQuotes([]);
      setSearchTerm('');
      setMaterials([]);
    } else {
      // Auto-verify saved session when modal opens
      const savedAuth = localStorage.getItem('vbx_admin_auth');
      const savedPwd = localStorage.getItem('vbx_admin_pwd');
      if (savedAuth === 'true' && savedPwd && !isAuthenticated) {
        setPassword(savedPwd);
        // Auto-verify the saved password
        axios.post(`${API}/admin/verify`, { password: savedPwd })
          .then(() => {
            setIsAuthenticated(true);
          })
          .catch(() => {
            // Saved password is no longer valid
            localStorage.removeItem('vbx_admin_auth');
            localStorage.removeItem('vbx_admin_pwd');
            setPassword('');
          });
      }
    }
  }, [isOpen]);

  // Load materials
  const loadMaterials = async () => {
    setIsLoadingMaterials(true);
    try {
      const response = await axios.get(`${API}/materials`);
      setMaterials(response.data);
    } catch (error) {
      console.error('Error loading materials:', error);
      toast.error('Error al cargar materiales');
    } finally {
      setIsLoadingMaterials(false);
    }
  };

  // Load invoices
  const loadInvoices = async () => {
    setIsLoadingInvoices(true);
    try {
      const [invoicesRes, creditsRes] = await Promise.all([
        axios.get(`${API}/invoicing/pending`),
        axios.get(`${API}/invoicing/credits`)
      ]);
      setInvoices(invoicesRes.data);
      setBlikonCredits(creditsRes.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const handleApproveInvoice = async (invoiceId) => {
    try {
      const res = await axios.post(`${API}/invoicing/approve/${invoiceId}`);
      if (res.data.success) {
        toast.success(`Factura generada. UUID: ${res.data.uuid || 'N/A'}`);
      } else {
        toast.error(res.data.message || 'Error al generar factura');
      }
      loadInvoices();
    } catch (error) {
      toast.error('Error al aprobar factura');
    }
  };

  const handleRejectInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoicing/reject/${invoiceId}`);
      toast.success('Solicitud rechazada');
      loadInvoices();
    } catch (error) {
      toast.error('Error al rechazar solicitud');
    }
  };

  const handleDownloadInvoicePDF = async (invoiceId) => {
    try {
      const res = await axios.get(`${API}/invoicing/download-pdf/${invoiceId}`);
      if (res.data.pdf_base64) {
        const byteCharacters = atob(res.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = res.data.filename || 'CFDI.pdf';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error('Error al descargar PDF');
    }
  };

  const handleDownloadInvoiceXML = async (invoiceId) => {
    try {
      const res = await axios.get(`${API}/invoicing/download-xml/${invoiceId}`);
      if (res.data.xml_content) {
        const blob = new Blob([res.data.xml_content], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = res.data.filename || 'CFDI.xml';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error('Error al descargar XML');
    }
  };

  const handleVerifyPassword = async () => {
    if (!password) {
      toast.error('Ingresa la contraseña');
      return;
    }

    setIsVerifying(true);
    try {
      await axios.post(`${API}/admin/verify`, { password });
      setIsAuthenticated(true);
      localStorage.setItem('vbx_admin_auth', 'true');
      localStorage.setItem('vbx_admin_pwd', password);
      toast.success('Acceso concedido');
    } catch (error) {
      toast.error('Contraseña incorrecta');
      setPassword('');
      localStorage.removeItem('vbx_admin_auth');
      localStorage.removeItem('vbx_admin_pwd');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    localStorage.removeItem('vbx_admin_auth');
    localStorage.removeItem('vbx_admin_pwd');
    toast.success('Sesión de administrador cerrada');
    onOpenChange(false);
  };

  const handlePackagePriceChange = (packageId, newPrice) => {
    setLocalSettings(prev => ({
      ...prev,
      packages: prev.packages.map(pkg => 
        pkg.id === packageId 
          ? { ...pkg, base_price: parseFloat(newPrice) || 0 }
          : pkg
      )
    }));
  };

  const handleFactorChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };

  // Separate handler for boolean values (toggles/switches)
  const handleBooleanChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: Boolean(value)
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(localSettings, password);
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setLocalSettings({
      ...localSettings,
      ...DEFAULT_SETTINGS,
      packages: localSettings.packages.map((pkg, idx) => ({
        ...pkg,
        base_price: DEFAULT_SETTINGS.packages[idx]?.base_price || pkg.base_price
      }))
    });
  };

  // Load quotes when switching to history tab
  const loadQuotes = async () => {
    setIsLoadingQuotes(true);
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      toast.error('Error al cargar cotizaciones');
    } finally {
      setIsLoadingQuotes(false);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta cotización?')) return;
    
    try {
      await axios.delete(`${API}/quotes/${quoteId}`);
      setQuotes(prev => prev.filter(q => q.id !== quoteId));
      toast.success('Cotización eliminada');
    } catch (error) {
      toast.error('Error al eliminar cotización');
    }
  };

  const handleMarkPaid = async (quote) => {
    const hasReferral = !!quote.referrer_code;
    const confirmMsg = hasReferral
      ? `Marcar como pagada y enviar comisión al referido "${quote.referrer_name || quote.referrer_code}"?`
      : '¿Marcar esta cotización como pagada?';
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await axios.patch(`${API}/quotes/${quote.id}/mark-paid`);
      setQuotes(prev => prev.map(q => q.id === quote.id ? { ...q, status: 'paid', paid_at: new Date().toISOString(), commission_sent_at: res.data.commission_sent ? new Date().toISOString() : q.commission_sent_at } : q));
      if (res.data.commission_sent) {
        toast.success('Cotización pagada. Comisión enviada al referido.');
      } else if (hasReferral) {
        toast.warning('Cotización pagada. Comisión no pudo enviarse (revisar logs).');
      } else {
        toast.success('Cotización marcada como pagada');
      }
    } catch (e) {
      toast.error('Error al marcar como pagada');
    }
  };

  const exportToCSV = () => {
    if (quotes.length === 0) {
      toast.error('No hay cotizaciones para exportar');
      return;
    }

    const headers = ['Fecha', 'Código', 'Cliente', 'Email', 'Título', 'Autor', 'Páginas', 'Palabras', 'Duración (hrs)', 'Paquete', 'Precio Base', 'Descuento %', 'Total', 'Moneda'];
    
    const rows = quotes.map(q => [
      new Date(q.created_at).toLocaleDateString('es-MX'),
      q.recovery_code || '',
      q.customer_name || '',
      q.customer_email || '',
      q.book_title || '',
      q.author_name || '',
      q.page_count || 0,
      q.word_count || 0,
      q.estimated_duration_hours || 0,
      q.package_name || q.package_id || '',
      q.base_price || 0,
      q.custom_discount || 0,
      q.grand_total || 0,
      q.currency || 'MXN'
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell ?? ''}"`).join(','))
      .join('\n');

    // Add BOM for UTF-8 encoding (fixes special characters in Excel)
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `cotizaciones_viubux_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success(`CSV descargado (${quotes.length} cotizaciones)`);
  };

  const filteredQuotes = quotes.filter(q => {
    if (!searchTerm || searchTerm.trim() === '' || searchTerm.trim() === '*') return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (q.book_title || '').toLowerCase().includes(searchLower) ||
      (q.author_name || '').toLowerCase().includes(searchLower) ||
      (q.customer_name || '').toLowerCase().includes(searchLower) ||
      (q.customer_email || '').toLowerCase().includes(searchLower) ||
      (q.recovery_code || '').toLowerCase().includes(searchLower)
    );
  });

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-slate-400 hover:text-white hover:bg-white/10"
          data-testid="btn-settings"
        >
          <Settings className="w-5 h-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0f133a] border-[#272b96] text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2 justify-between" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#aa2aff]" />
              Configuración
            </div>
            {isAuthenticated && (
              <button
                onClick={handleLogout}
                className="text-xs text-slate-400 hover:text-red-400 transition-colors flex items-center gap-1 font-normal"
                data-testid="btn-admin-logout"
              >
                <LogOut className="w-3.5 h-3.5" />
                Cerrar sesión
              </button>
            )}
          </DialogTitle>
        </DialogHeader>

        {!isAuthenticated ? (
          <div className="mt-6 space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#aa2aff]/10 rounded-full flex items-center justify-center">
                <Lock className="w-8 h-8 text-[#aa2aff]" />
              </div>
              <p className="text-slate-400">
                Ingresa la contraseña de administrador para acceder.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="admin-password" className="text-slate-300">
                Contraseña de Administrador
              </Label>
              <Input
                id="admin-password"
                type="password"
                data-testid="input-admin-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleVerifyPassword()}
                className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff]"
              />
            </div>

            <Button
              onClick={handleVerifyPassword}
              disabled={isVerifying || !password}
              className="w-full bg-[#aa2aff] hover:bg-[#7012b2] text-white"
              data-testid="btn-verify-password"
            >
              {isVerifying ? 'Verificando...' : (
                <>
                  <Unlock className="w-4 h-4 mr-2" />
                  Acceder
                </>
              )}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mt-2 text-sm text-green-400">
              <Unlock className="w-4 h-4" />
              <span>Acceso de administrador activo</span>
            </div>

            <Tabs defaultValue="prices" className="mt-4" onValueChange={(val) => val === 'history' && loadQuotes()}>
              <TabsList className="bg-[#080c2d] border border-white/10 grid grid-cols-8">
                <TabsTrigger 
                  value="prices"
                  className="data-[state=active]:bg-[#aa2aff] data-[state=active]:text-white text-xs"
                >
                  Paquetes
                </TabsTrigger>
                <TabsTrigger 
                  value="services"
                  className="data-[state=active]:bg-[#aa2aff] data-[state=active]:text-white text-xs"
                >
                  Servicios
                </TabsTrigger>
                <TabsTrigger 
                  value="factors"
                  className="data-[state=active]:bg-[#aa2aff] data-[state=active]:text-white text-xs"
                >
                  Factores
                </TabsTrigger>
                <TabsTrigger 
                  value="options"
                  className="data-[state=active]:bg-[#aa2aff] data-[state=active]:text-white text-xs"
                >
                  Opciones
                </TabsTrigger>
                <TabsTrigger 
                  value="users"
                  className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white text-xs"
                >
                  Usuarios
                </TabsTrigger>
                <TabsTrigger 
                  value="invoicing"
                  className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-xs"
                  onClick={loadInvoices}
                >
                  Facturas
                </TabsTrigger>
                <TabsTrigger 
                  value="materials"
                  className="data-[state=active]:bg-[#aa2aff] data-[state=active]:text-white text-xs"
                >
                  Materiales
                </TabsTrigger>
                <TabsTrigger 
                  value="history"
                  className="data-[state=active]:bg-[#aa2aff] data-[state=active]:text-white text-xs"
                >
                  Historial
                </TabsTrigger>
              </TabsList>

              <TabsContent value="prices" className="mt-4 space-y-4">
                <p className="text-sm text-slate-400">
                  Modifica los precios base de cada paquete de Audiolibro (en pesos MXN por hora).
                </p>
                
                {localSettings.packages?.map((pkg) => (
                  <div key={pkg.id} className="space-y-2">
                    <Label htmlFor={`price-${pkg.id}`} className="text-slate-300 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#aa2aff]" />
                      {pkg.name}
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <Input
                        id={`price-${pkg.id}`}
                        type="number"
                        data-testid={`input-price-${pkg.id}`}
                        value={pkg.base_price}
                        onChange={(e) => handlePackagePriceChange(pkg.id, e.target.value)}
                        className="bg-[#080c2d]/50 border-[#272b96] text-white pl-8 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff]"
                      />
                    </div>
                  </div>
                ))}

                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={handleReset}
                    className="flex-1 border-slate-600 text-slate-300 hover:bg-white/5"
                    data-testid="btn-reset-settings"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restablecer
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 bg-[#aa2aff] hover:bg-[#7012b2] text-white"
                    data-testid="btn-save-settings"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isSaving ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </TabsContent>

              {/* NEW: Services Prices Tab */}
              <TabsContent value="services" className="mt-4 space-y-4 max-h-[400px] overflow-y-auto pr-2">
                <p className="text-sm text-slate-400">
                  Modifica los precios de todos los servicios adicionales.
                </p>

                {/* Distribución */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">📦 Distribución</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Essential</Label>
                      <Input
                        type="number"
                        value={localSettings.price_dist_essential || 697}
                        onChange={(e) => handleFactorChange('price_dist_essential', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Platino</Label>
                      <Input
                        type="number"
                        value={localSettings.price_dist_platino || 1697}
                        onChange={(e) => handleFactorChange('price_dist_platino', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Premium</Label>
                      <Input
                        type="number"
                        value={localSettings.price_dist_premium || 2997}
                        onChange={(e) => handleFactorChange('price_dist_premium', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                  </div>
                </div>

                {/* Booktrailer */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">🎬 Booktrailer</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Essential</Label>
                      <Input
                        type="number"
                        value={localSettings.price_bt_essential || 697}
                        onChange={(e) => handleFactorChange('price_bt_essential', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Platino</Label>
                      <Input
                        type="number"
                        value={localSettings.price_bt_platino || 4997}
                        onChange={(e) => handleFactorChange('price_bt_platino', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Premium</Label>
                      <Input
                        type="number"
                        value={localSettings.price_bt_premium || 8997}
                        onChange={(e) => handleFactorChange('price_bt_premium', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                  </div>
                </div>

                {/* Kit Legal */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">⚖️ Kit Legal</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Registro de Obra</Label>
                      <Input
                        type="number"
                        value={localSettings.price_legal_registro || 2500}
                        onChange={(e) => handleFactorChange('price_legal_registro', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Solicitud ISBN</Label>
                      <Input
                        type="number"
                        value={localSettings.price_legal_isbn || 2000}
                        onChange={(e) => handleFactorChange('price_legal_isbn', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Combo</Label>
                      <Input
                        type="number"
                        value={localSettings.price_legal_combo || 4000}
                        onChange={(e) => handleFactorChange('price_legal_combo', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                  </div>
                </div>

                {/* Diseño de Portada */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">🎨 Diseño de Portada</h4>
                  <div className="grid grid-cols-1 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Diseño de Portada</Label>
                      <Input
                        type="number"
                        value={localSettings.price_cover_design || 1250}
                        onChange={(e) => handleFactorChange('price_cover_design', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8 w-32"
                      />
                    </div>
                  </div>
                </div>

                {/* Pack Branding */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">✨ Pack Branding</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Essential</Label>
                      <Input
                        type="number"
                        value={localSettings.price_branding_essential || 2500}
                        onChange={(e) => handleFactorChange('price_branding_essential', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Pro</Label>
                      <Input
                        type="number"
                        value={localSettings.price_branding_pro || 4500}
                        onChange={(e) => handleFactorChange('price_branding_pro', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Premium</Label>
                      <Input
                        type="number"
                        value={localSettings.price_branding_premium || 7500}
                        onChange={(e) => handleFactorChange('price_branding_premium', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                  </div>
                </div>

                {/* Música y Consultoría */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">🎵 Música y Consultoría</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Música de Stock (por pieza)</Label>
                      <Input
                        type="number"
                        value={localSettings.price_music_stock || 797}
                        onChange={(e) => handleFactorChange('price_music_stock', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Consultoría (por sesión)</Label>
                      <Input
                        type="number"
                        value={localSettings.price_consultoria || 850}
                        onChange={(e) => handleFactorChange('price_consultoria', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                  </div>
                </div>

                {/* Podcast Paquetes */}

                {/* New Additional Services */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">Servicios Adicionales (Nuevos)</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">QR Dinámico</Label>
                      <Input type="number" value={localSettings.price_qr_dinamico || 397} onChange={(e) => handleFactorChange('price_qr_dinamico', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Enlace Go.Viubux</Label>
                      <Input type="number" value={localSettings.price_enlace_go || 197} onChange={(e) => handleFactorChange('price_enlace_go', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">NFC VCard</Label>
                      <Input type="number" value={localSettings.price_nfc_vcard || 1997} onChange={(e) => handleFactorChange('price_nfc_vcard', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Separadores</Label>
                      <Input type="number" value={localSettings.price_separadores || 397} onChange={(e) => handleFactorChange('price_separadores', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Activar Preventa</Label>
                      <Input type="number" value={localSettings.price_preventa || 897} onChange={(e) => handleFactorChange('price_preventa', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Banner Promotion (/mes)</Label>
                      <Input type="number" value={localSettings.price_banner_promotion || 197} onChange={(e) => handleFactorChange('price_banner_promotion', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Diseño WEB Banner</Label>
                      <Input type="number" value={localSettings.price_diseno_web_banner || 997} onChange={(e) => handleFactorChange('price_diseno_web_banner', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Photoshoot 1hr</Label>
                      <Input type="number" value={localSettings.price_photoshoot || 2500} onChange={(e) => handleFactorChange('price_photoshoot', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Página de Autor</Label>
                      <Input type="number" value={localSettings.price_pagina_autor || 3500} onChange={(e) => handleFactorChange('price_pagina_autor', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Podcast Paquetes - original */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">🎙️ Podcast - Paquetes</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Essential</Label>
                      <Input
                        type="number"
                        value={localSettings.price_pod_essential || 997}
                        onChange={(e) => handleFactorChange('price_pod_essential', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Platino</Label>
                      <Input
                        type="number"
                        value={localSettings.price_pod_platino || 3500}
                        onChange={(e) => handleFactorChange('price_pod_platino', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Premium</Label>
                      <Input
                        type="number"
                        value={localSettings.price_pod_premium || 5500}
                        onChange={(e) => handleFactorChange('price_pod_premium', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                  </div>
                </div>

                {/* Podcast Addons */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">🎙️ Podcast - Addons</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Set Básico (único)</Label>
                      <Input
                        type="number"
                        value={localSettings.price_pod_set_basico || 850}
                        onChange={(e) => handleFactorChange('price_pod_set_basico', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Set Virtual (único)</Label>
                      <Input
                        type="number"
                        value={localSettings.price_pod_set_virtual || 350}
                        onChange={(e) => handleFactorChange('price_pod_set_virtual', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8"
                      />
                    </div>
                  </div>
                </div>

                {/* Renta de Equipo Prices */}
                <div className="bg-[#1a1f50]/30 rounded-lg p-3 border border-white/5">
                  <h4 className="text-white font-semibold mb-3 text-sm">🎤 Renta de Equipo</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Micrófono Shure (/u/día)</Label>
                      <Input type="number" value={localSettings.price_renta_microfono || 497} onChange={(e) => handleFactorChange('price_renta_microfono', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Cámara Canon (/día)</Label>
                      <Input type="number" value={localSettings.price_renta_camara || 1850} onChange={(e) => handleFactorChange('price_renta_camara', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Lavalier Sennheiser (/u/día)</Label>
                      <Input type="number" value={localSettings.price_renta_lavalier || 750} onChange={(e) => handleFactorChange('price_renta_lavalier', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Teleprompter (/día)</Label>
                      <Input type="number" value={localSettings.price_renta_teleprompter || 897} onChange={(e) => handleFactorChange('price_renta_teleprompter', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Transmisión en Vivo Prices */}
                <div className="bg-[#1a1f50]/30 rounded-lg p-3 border border-white/5">
                  <h4 className="text-white font-semibold mb-3 text-sm">📡 Transmisión en Vivo</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Basic Uniplataforma</Label>
                      <Input type="number" value={localSettings.price_transmision_basic || 697} onChange={(e) => handleFactorChange('price_transmision_basic', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Pro Multiplataforma (/día)</Label>
                      <Input type="number" value={localSettings.price_transmision_pro || 3997} onChange={(e) => handleFactorChange('price_transmision_pro', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Sonido Directo Prices */}
                <div className="bg-[#1a1f50]/30 rounded-lg p-3 border border-white/5">
                  <h4 className="text-white font-semibold mb-3 text-sm">🔊 Sonido Directo</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">BásicoPro (/hr)</Label>
                      <Input type="number" value={localSettings.price_sonido_basicopro || 350} onChange={(e) => handleFactorChange('price_sonido_basicopro', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Pro (/hr)</Label>
                      <Input type="number" value={localSettings.price_sonido_pro || 997} onChange={(e) => handleFactorChange('price_sonido_pro', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Foro Prices */}
                <div className="bg-[#1a1f50]/30 rounded-lg p-3 border border-white/5">
                  <h4 className="text-white font-semibold mb-3 text-sm">🏢 Foro</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Foro Santa Fe (/hr)</Label>
                      <Input type="number" value={localSettings.price_foro_santa_fe || 1000} onChange={(e) => handleFactorChange('price_foro_santa_fe', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Foro Masivo (/día)</Label>
                      <Input type="number" value={localSettings.price_foro_masivo || 30000} onChange={(e) => handleFactorChange('price_foro_masivo', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Storage (/día)</Label>
                      <Input type="number" value={localSettings.price_foro_storage || 97} onChange={(e) => handleFactorChange('price_foro_storage', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Interpretación Prices */}
                <div className="bg-[#1a1f50]/30 rounded-lg p-3 border border-white/5">
                  <h4 className="text-white font-semibold mb-3 text-sm">🌐 Interpretación Simultánea</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Intérprete Bilingüe (/hr)</Label>
                      <Input type="number" value={localSettings.price_interprete || 1500} onChange={(e) => handleFactorChange('price_interprete', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Cabina de Audio (/día)</Label>
                      <Input type="number" value={localSettings.price_cabina_audio || 7500} onChange={(e) => handleFactorChange('price_cabina_audio', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Equipo TX/RX (/u/día)</Label>
                      <Input type="number" value={localSettings.price_equipo_tx || 450} onChange={(e) => handleFactorChange('price_equipo_tx', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Técnico Audio (/día)</Label>
                      <Input type="number" value={localSettings.price_tecnico_interp || 1500} onChange={(e) => handleFactorChange('price_tecnico_interp', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Sonorización Prices */}
                <div className="bg-[#1a1f50]/30 rounded-lg p-3 border border-white/5">
                  <h4 className="text-white font-semibold mb-3 text-sm">🔈 Sonorización Eventos</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Essential (/día)</Label>
                      <Input type="number" value={localSettings.price_sono_essential || 3500} onChange={(e) => handleFactorChange('price_sono_essential', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Plus (/día)</Label>
                      <Input type="number" value={localSettings.price_sono_plus || 7500} onChange={(e) => handleFactorChange('price_sono_plus', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Premium (/día)</Label>
                      <Input type="number" value={localSettings.price_sono_premium || 17500} onChange={(e) => handleFactorChange('price_sono_premium', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Voz Clonada & Multivoz */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-cyan-500/20">
                  <h4 className="text-white font-semibold mb-3 text-sm">🎤 Voz Clonada & Multivoz</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Clonación de Voz Original</Label>
                      <Input type="number" value={localSettings.price_voice_cloning || 7997} onChange={(e) => handleFactorChange('price_voice_cloning', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Voz Clonada Adicional (/u)</Label>
                      <Input type="number" value={localSettings.price_extra_voice || 697} onChange={(e) => handleFactorChange('price_extra_voice', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Multivoz Booking Fee</Label>
                      <Input type="number" value={localSettings.price_multivoz_booking || 997} onChange={(e) => handleFactorChange('price_multivoz_booking', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Multivoz por Minuto (/min)</Label>
                      <Input type="number" value={localSettings.price_multivoz_per_min || 50} onChange={(e) => handleFactorChange('price_multivoz_per_min', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Talento Premium (/hora)</Label>
                      <Input type="number" value={localSettings.price_talento_premium || 1500} onChange={(e) => handleFactorChange('price_talento_premium', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Revisión de Audio */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">🔍 Revisión de Audio para Distribución</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">0-7 hrs</Label>
                      <Input type="number" value={localSettings.price_revision_7 || 697} onChange={(e) => handleFactorChange('price_revision_7', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">7.1-10 hrs</Label>
                      <Input type="number" value={localSettings.price_revision_10 || 997} onChange={(e) => handleFactorChange('price_revision_10', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">10.1-20 hrs</Label>
                      <Input type="number" value={localSettings.price_revision_20 || 1497} onChange={(e) => handleFactorChange('price_revision_20', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Masterización */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">🎛️ Masterización de Audiolibro</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">0-3.5 hrs</Label>
                      <Input type="number" value={localSettings.price_master_3 || 1000} onChange={(e) => handleFactorChange('price_master_3', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">3.6-7 hrs</Label>
                      <Input type="number" value={localSettings.price_master_7 || 1500} onChange={(e) => handleFactorChange('price_master_7', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">7.1-10 hrs</Label>
                      <Input type="number" value={localSettings.price_master_10 || 1850} onChange={(e) => handleFactorChange('price_master_10', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">10.1-20 hrs</Label>
                      <Input type="number" value={localSettings.price_master_20 || 2500} onChange={(e) => handleFactorChange('price_master_20', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Imprenta */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">🖨️ Imprenta & Envío</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Tarjetas 1000u</Label>
                      <Input type="number" value={localSettings.price_tarjetas || 697} onChange={(e) => handleFactorChange('price_tarjetas', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Separadores 500u</Label>
                      <Input type="number" value={localSettings.price_separadores_impresos || 797} onChange={(e) => handleFactorChange('price_separadores_impresos', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Envío a Domicilio</Label>
                      <Input type="number" value={localSettings.price_envio || 150} onChange={(e) => handleFactorChange('price_envio', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Pautaje Digital */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">📱 Pautaje Digital</h4>
                  <div className="grid grid-cols-1 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Pautaje Digital Redes + Google</Label>
                      <Input type="number" value={localSettings.price_pautaje_digital || 2000} onChange={(e) => handleFactorChange('price_pautaje_digital', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8 w-32" />
                    </div>
                  </div>
                </div>

                {/* Studio Fees */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">🏠 Studio Fees</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Studio Premium (/hr)</Label>
                      <Input type="number" value={localSettings.price_studio_premium || 3500} onChange={(e) => handleFactorChange('price_studio_premium', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Studio Essential (/hr)</Label>
                      <Input type="number" value={localSettings.price_studio_essential || 2000} onChange={(e) => handleFactorChange('price_studio_essential', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">AudioPost (/hr)</Label>
                      <Input type="number" value={localSettings.price_audio_post || 1500} onChange={(e) => handleFactorChange('price_audio_post', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Commission-Free Monthly Pass */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">🎟️ Commission-Free Monthly Pass</h4>
                  <div className="grid grid-cols-1 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Commission-Free Pass</Label>
                      <Input type="number" value={localSettings.price_commission_free || 1500} onChange={(e) => handleFactorChange('price_commission_free', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8 w-32" />
                    </div>
                  </div>
                </div>

                {/* Servicios Editoriales */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">📖 Servicios Editoriales</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Maquetación Estándar</Label>
                      <Input type="number" value={localSettings.price_maquetacion_estandar || 3997} onChange={(e) => handleFactorChange('price_maquetacion_estandar', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Maquetación Premium</Label>
                      <Input type="number" value={localSettings.price_maquetacion_premium || 6997} onChange={(e) => handleFactorChange('price_maquetacion_premium', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Corrección Estilo (/cuartilla)</Label>
                      <Input type="number" value={localSettings.price_correccion_estilo || 90} onChange={(e) => handleFactorChange('price_correccion_estilo', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Corrección Ortotipo (/cuartilla)</Label>
                      <Input type="number" value={localSettings.price_correccion_ortotipo || 50} onChange={(e) => handleFactorChange('price_correccion_ortotipo', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Portada Física</Label>
                      <Input type="number" value={localSettings.price_portada_fisica || 2500} onChange={(e) => handleFactorChange('price_portada_fisica', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Ghost Writing</Label>
                      <Input type="number" value={localSettings.price_editorial_ghost_writing || 287000} onChange={(e) => handleFactorChange('price_editorial_ghost_writing', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Walking Podcast */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">🚶 Walking Podcast</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Essential</Label>
                      <Input type="number" value={localSettings.price_walking_essential || 2997} onChange={(e) => handleFactorChange('price_walking_essential', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Platino</Label>
                      <Input type="number" value={localSettings.price_walking_platino || 4997} onChange={(e) => handleFactorChange('price_walking_platino', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Premium</Label>
                      <Input type="number" value={localSettings.price_walking_premium || 6997} onChange={(e) => handleFactorChange('price_walking_premium', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Sonido Directo (/día)</Label>
                      <Input type="number" value={localSettings.price_walking_sonido || 3500} onChange={(e) => handleFactorChange('price_walking_sonido', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Asistente (/día)</Label>
                      <Input type="number" value={localSettings.price_walking_asistente || 1500} onChange={(e) => handleFactorChange('price_walking_asistente', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* Meditaciones y AudioCursos */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">🧘 Meditaciones & Cursos (A/V)</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Meditación Essential</Label>
                      <Input type="number" value={localSettings.price_med_essential || 997} onChange={(e) => handleFactorChange('price_med_essential', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Meditación Platino</Label>
                      <Input type="number" value={localSettings.price_med_platino || 1997} onChange={(e) => handleFactorChange('price_med_platino', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Meditación Premium</Label>
                      <Input type="number" value={localSettings.price_med_premium || 2997} onChange={(e) => handleFactorChange('price_med_premium', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">AudioCurso Essential</Label>
                      <Input type="number" value={localSettings.price_ac_essential || 997} onChange={(e) => handleFactorChange('price_ac_essential', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">AudioCurso Platino</Label>
                      <Input type="number" value={localSettings.price_ac_platino || 1997} onChange={(e) => handleFactorChange('price_ac_platino', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">AudioCurso Premium</Label>
                      <Input type="number" value={localSettings.price_ac_premium || 2997} onChange={(e) => handleFactorChange('price_ac_premium', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                {/* ViubuxConnect */}
                <div className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <h4 className="text-white font-semibold mb-3 text-sm">🔗 ViubuxConnect</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Inscripción ViubuxConnect</Label>
                      <Input type="number" value={localSettings.price_viubux_connect || 5879} onChange={(e) => handleFactorChange('price_viubux_connect', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Catálogo 12 meses</Label>
                      <Input type="number" value={localSettings.price_vc_catalogo || 2248} onChange={(e) => handleFactorChange('price_vc_catalogo', e.target.value)} className="bg-[#080c2d] border-[#272b96] text-white text-sm h-8" />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6 sticky bottom-0 bg-[#0a0e1a] py-3">
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 bg-[#aa2aff] hover:bg-[#7012b2] text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isSaving ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="factors" className="mt-4 space-y-4">
                <p className="text-sm text-slate-400">
                  Ajusta los factores de conversión para el cálculo de duración.
                </p>
                
                <div className="space-y-2">
                  <Label htmlFor="words_per_hour" className="text-slate-300">
                    Palabras por hora (1 hora = X palabras)
                  </Label>
                  <Input
                    id="words_per_hour"
                    type="number"
                    data-testid="input-words-per-hour"
                    value={localSettings.words_per_hour || 9005}
                    onChange={(e) => handleFactorChange('words_per_hour', e.target.value)}
                    className="bg-[#080c2d]/50 border-[#272b96] text-white focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pages_per_hour" className="text-slate-300">
                    Páginas por hora (1 hora = X páginas)
                  </Label>
                  <Input
                    id="pages_per_hour"
                    type="number"
                    data-testid="input-pages-per-hour"
                    value={localSettings.pages_per_hour || 30}
                    onChange={(e) => handleFactorChange('pages_per_hour', e.target.value)}
                    className="bg-[#080c2d]/50 border-[#272b96] text-white focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff]"
                  />
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={handleReset}
                    className="flex-1 border-slate-600 text-slate-300 hover:bg-white/5"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restablecer
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 bg-[#aa2aff] hover:bg-[#7012b2] text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isSaving ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="options" className="mt-4 space-y-4">
                <p className="text-sm text-slate-400">
                  Opciones de visualización, descuentos y tipo de cambio.
                </p>
                
                {/* Exchange Rate */}
                <div className="p-4 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <div className="flex items-center gap-3 mb-3">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    <div>
                      <Label className="text-slate-300">Tipo de Cambio MXN/USD</Label>
                      <p className="text-xs text-slate-500">Para conversión de precios a dólares</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">1 USD =</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="1"
                      value={localSettings.usd_exchange_rate || 17.5}
                      onChange={(e) => handleFactorChange('usd_exchange_rate', e.target.value)}
                      className="bg-[#080c2d] border-[#272b96] text-white w-24 focus:border-[#aa2aff]"
                      data-testid="input-exchange-rate"
                    />
                    <span className="text-slate-400">MXN</span>
                  </div>
                </div>

                {/* Section Visibility */}
                <div className="p-4 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="w-5 h-5 text-[#aa2aff]" />
                    <div>
                      <Label className="text-slate-300">Visibilidad de Secciones</Label>
                      <p className="text-xs text-slate-500">Ocultar o mostrar secciones del cotizador</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Sección Audiolibro</span>
                      <Switch
                        checked={localSettings.show_audiobook_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_audiobook_section', checked)}
                        data-testid="switch-show-audiobook"
                      />
                    </div>
                    <div className="flex items-center justify-between pl-4">
                      <span className="text-sm text-slate-400">Paquete Ultra</span>
                      <Switch
                        checked={localSettings.show_ultra_package !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_ultra_package', checked)}
                        data-testid="switch-show-ultra"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Sección Podcast</span>
                      <Switch
                        checked={localSettings.show_podcast_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_podcast_section', checked)}
                        data-testid="switch-show-podcast"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Sección Servicios Adicionales</span>
                      <Switch
                        checked={localSettings.show_servicios_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_servicios_section', checked)}
                        data-testid="switch-show-servicios"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Sección Meditaciones Guiadas</span>
                      <Switch
                        checked={localSettings.show_meditaciones_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_meditaciones_section', checked)}
                        data-testid="switch-show-meditaciones"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Sección Cursos (A/V)</span>
                      <Switch
                        checked={localSettings.show_audiocursos_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_audiocursos_section', checked)}
                        data-testid="switch-show-audiocursos"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Inscripción ViubuxConnect</span>
                      <Switch
                        checked={localSettings.show_viubux_connect_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_viubux_connect_section', checked)}
                        data-testid="switch-show-viubux-connect"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Servicios Editoriales</span>
                      <Switch
                        checked={localSettings.show_editorial_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_editorial_section', checked)}
                        data-testid="switch-show-editorial"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Transferencia Bancaria</span>
                      <Switch
                        checked={localSettings.show_bank_transfer !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_bank_transfer', checked)}
                        data-testid="switch-show-bank-transfer"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Walking Podcast</span>
                      <Switch
                        checked={localSettings.show_walking_podcast_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_walking_podcast_section', checked)}
                        data-testid="switch-show-walking-podcast"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Renta de Equipo</span>
                      <Switch
                        checked={localSettings.show_renta_equipo_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_renta_equipo_section', checked)}
                        data-testid="switch-show-renta-equipo"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Transmisión en Vivo</span>
                      <Switch
                        checked={localSettings.show_transmision_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_transmision_section', checked)}
                        data-testid="switch-show-transmision"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Sonido Directo</span>
                      <Switch
                        checked={localSettings.show_sonido_directo_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_sonido_directo_section', checked)}
                        data-testid="switch-show-sonido-directo"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Foro</span>
                      <Switch
                        checked={localSettings.show_foro_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_foro_section', checked)}
                        data-testid="switch-show-foro"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Interpretación Simultánea</span>
                      <Switch
                        checked={localSettings.show_interpretacion_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_interpretacion_section', checked)}
                        data-testid="switch-show-interpretacion"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Sonorización Eventos</span>
                      <Switch
                        checked={localSettings.show_sonorizacion_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_sonorizacion_section', checked)}
                        data-testid="switch-show-sonorizacion"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Servicios de Voz</span>
                      <Switch
                        checked={localSettings.show_voice_services_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_voice_services_section', checked)}
                        data-testid="switch-show-voice-services"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Studio Fees</span>
                      <Switch
                        checked={localSettings.show_studio_fees_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_studio_fees_section', checked)}
                        data-testid="switch-show-studio-fees"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Hand-Free</span>
                      <Switch
                        checked={localSettings.show_hand_free_section !== false}
                        onCheckedChange={(checked) => handleBooleanChange('show_hand_free_section', checked)}
                        data-testid="switch-show-hand-free"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <div className="flex items-center gap-3">
                    <Percent className="w-5 h-5 text-[#aa2aff]" />
                    <div>
                      <Label className="text-slate-300">Mostrar Descuentos Adicionales</Label>
                      <p className="text-xs text-slate-500">Habilita la sección de descuentos manuales (15%, 30%)</p>
                    </div>
                  </div>
                  <Switch
                    checked={showDiscounts}
                    onCheckedChange={onToggleDiscounts}
                    data-testid="switch-show-discounts"
                  />
                </div>

                {/* Co-Producción */}
                <div className="p-4 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Handshake className="w-5 h-5 text-amber-400" />
                      <div>
                        <Label className="text-slate-300">Co-Producción</Label>
                        <p className="text-xs text-slate-500">Mostrar aportación de co-inversionista en la cotización</p>
                      </div>
                    </div>
                    <Switch
                      checked={localSettings.show_coproduccion === true}
                      onCheckedChange={(checked) => handleBooleanChange('show_coproduccion', checked)}
                      data-testid="switch-show-coproduccion"
                    />
                  </div>
                  {localSettings.show_coproduccion && (
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/5">
                      <span className="text-sm text-slate-400">Porcentaje del Co-Inversionista:</span>
                      <Input
                        type="number"
                        min="1"
                        max="99"
                        value={localSettings.coproduccion_percent || 50}
                        onChange={(e) => handleFactorChange('coproduccion_percent', e.target.value)}
                        className="bg-[#080c2d] border-[#272b96] text-white w-20 focus:border-[#aa2aff]"
                        data-testid="input-coproduccion-percent"
                      />
                      <span className="text-slate-400">%</span>
                    </div>
                  )}
                </div>

                {/* Custom Global Discount */}
                <div className="p-4 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <div className="flex items-center gap-3 mb-3">
                    <Percent className="w-5 h-5 text-green-400" />
                    <div>
                      <Label className="text-slate-300">Descuento Global Personalizado</Label>
                      <p className="text-xs text-slate-500">Se aplica al TOTAL (producción + servicios adicionales)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center bg-[#080c2d] border border-[#272b96] rounded-md overflow-hidden">
                      <button
                        onClick={() => onCustomDiscountTypeChange('percent')}
                        className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                          (customDiscountType || 'percent') === 'percent'
                            ? 'bg-[#aa2aff] text-white'
                            : 'text-slate-400 hover:text-white'
                        }`}
                        data-testid="btn-discount-type-percent"
                      >
                        %
                      </button>
                      <button
                        onClick={() => onCustomDiscountTypeChange('fixed')}
                        className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                          (customDiscountType || 'percent') === 'fixed'
                            ? 'bg-[#aa2aff] text-white'
                            : 'text-slate-400 hover:text-white'
                        }`}
                        data-testid="btn-discount-type-fixed"
                      >
                        $
                      </button>
                    </div>
                    <Input
                      type="number"
                      min="0"
                      max={customDiscountType === 'fixed' ? 999999 : 100}
                      value={localCustomDiscount}
                      onChange={(e) => setLocalCustomDiscount(parseFloat(e.target.value) || 0)}
                      className="bg-[#080c2d] border-[#272b96] text-white w-28 focus:border-[#aa2aff]"
                      data-testid="input-custom-discount"
                    />
                    <span className="text-slate-400">{(customDiscountType || 'percent') === 'percent' ? '%' : 'fijo'}</span>
                    <Button
                      onClick={() => onCustomDiscountChange(localCustomDiscount)}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="btn-apply-custom-discount"
                    >
                      Aplicar
                    </Button>
                    {localCustomDiscount > 0 && (
                      <Button
                        onClick={() => {
                          setLocalCustomDiscount(0);
                          onCustomDiscountChange(0);
                        }}
                        size="sm"
                        variant="outline"
                        className="border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        Quitar
                      </Button>
                    )}
                  </div>
                </div>

                {/* Database Backup */}
                <div className="p-4 bg-[#080c2d]/50 rounded-lg border border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Download className="w-5 h-5 text-green-400" />
                      <div>
                        <Label className="text-slate-300">Backup de Base de Datos</Label>
                        <p className="text-xs text-slate-500">Descarga toda la información en JSON</p>
                      </div>
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/admin/backup?password=${password}`, { timeout: 30000 });
                          const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `viubux_backup_${new Date().toISOString().split('T')[0]}.json`;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success(`Backup descargado (${res.data.total_documents} documentos)`);
                        } catch (e) {
                          toast.error('Error al generar backup');
                        }
                      }}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="btn-backup-db"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Descargar
                    </Button>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 bg-[#aa2aff] hover:bg-[#7012b2] text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isSaving ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </div>
              </TabsContent>

              {/* Users Tab */}
              <TabsContent value="users" className="mt-4 space-y-4 max-h-[400px] overflow-y-auto pr-2">
                <p className="text-sm text-slate-400 mb-3">
                  Gestiona los usuarios que pueden acceder al cotizador. Contraseña genérica para todos: <code className="bg-[#080c2d] px-2 py-0.5 rounded text-cyan-400">Viubux4u</code>
                </p>
                
                {/* Add User Form */}
                <div className="flex gap-2">
                  <Input
                    id="new-user-email"
                    placeholder="correo@ejemplo.com"
                    className="bg-[#080c2d] border-[#272b96] text-white flex-1"
                    data-testid="input-new-user-email"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const input = e.target;
                        const emailVal = input.value.trim();
                        if (!emailVal || !emailVal.includes('@')) return;
                        axios.post(`${API}/auth/users`, {
                          email: emailVal,
                          name: emailVal.split('@')[0],
                          admin_password: password
                        }).then(() => {
                          toast.success(`Acceso otorgado a ${emailVal}`);
                          input.value = '';
                          // Refresh user list
                          axios.get(`${API}/auth/users?password=${password}`).then(r => setAllowedUsers(r.data));
                        }).catch(err => toast.error(err.response?.data?.detail || 'Error al agregar'));
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    data-testid="btn-add-user"
                    onClick={() => {
                      const input = document.getElementById('new-user-email');
                      const emailVal = input?.value?.trim();
                      if (!emailVal || !emailVal.includes('@')) {
                        toast.error('Ingresa un correo válido');
                        return;
                      }
                      axios.post(`${API}/auth/users`, {
                        email: emailVal,
                        name: emailVal.split('@')[0],
                        admin_password: password
                      }).then(() => {
                        toast.success(`Acceso otorgado a ${emailVal}`);
                        input.value = '';
                        axios.get(`${API}/auth/users?password=${password}`).then(r => setAllowedUsers(r.data));
                      }).catch(err => toast.error(err.response?.data?.detail || 'Error al agregar'));
                    }}
                  >
                    Agregar
                  </Button>
                </div>

                {/* Users List */}
                <div className="space-y-2">
                  {allowedUsers.map((u) => (
                    <div key={u.email} className="flex items-center justify-between p-3 bg-[#080c2d]/50 rounded-lg border border-white/10">
                      <div>
                        <p className="text-sm text-white font-medium">{u.name || u.email}</p>
                        <p className="text-xs text-slate-500">{u.email}</p>
                        {u.last_login && (
                          <p className="text-xs text-slate-600">Último acceso: {new Date(u.last_login).toLocaleDateString('es-MX')}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {u.role === 'admin' && (
                          <span className="text-xs bg-[#aa2aff]/20 text-[#aa2aff] px-2 py-0.5 rounded">Admin</span>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`btn-remove-user-${u.email}`}
                          onClick={() => {
                            if (!window.confirm(`¿Revocar acceso a ${u.email}?`)) return;
                            axios.delete(`${API}/auth/users`, {
                              data: { email: u.email, admin_password: password }
                            }).then(() => {
                              toast.success(`Acceso revocado a ${u.email}`);
                              axios.get(`${API}/auth/users?password=${password}`).then(r => setAllowedUsers(r.data));
                            }).catch(err => toast.error(err.response?.data?.detail || 'Error'));
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {allowedUsers.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4">No hay usuarios registrados</p>
                  )}
                </div>
              </TabsContent>

              {/* Facturación Tab */}
              <TabsContent value="invoicing" className="mt-4 space-y-4 max-h-[400px] overflow-y-auto pr-2">
                {/* Credits Info */}
                {blikonCredits && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="text-green-400 text-sm font-medium">Créditos Blikon FEL</p>
                      <p className="text-slate-400 text-xs">Facturas disponibles para timbrar</p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 text-2xl font-bold">{blikonCredits.credits_remaining || '0'}</p>
                      <p className="text-slate-500 text-xs">Usados: {blikonCredits.credits_used || '0'}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-400">
                    Solicitudes de factura pendientes y completadas
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadInvoices}
                    className="border-white/20 text-white hover:bg-white/10 text-xs"
                    data-testid="btn-refresh-invoices"
                  >
                    {isLoadingInvoices ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                    Actualizar
                  </Button>
                </div>

                {invoices.length === 0 ? (
                  <p className="text-center text-slate-500 text-sm py-8">No hay solicitudes de factura</p>
                ) : (
                  <div className="space-y-3">
                    {invoices.map((inv) => (
                      <div key={inv.id} className={`p-3 rounded-lg border ${
                        inv.status === 'pending' ? 'bg-amber-500/5 border-amber-500/30' :
                        inv.status === 'completed' ? 'bg-green-500/5 border-green-500/30' :
                        inv.status === 'rejected' ? 'bg-red-500/5 border-red-500/30' :
                        inv.status === 'failed' ? 'bg-red-500/5 border-red-500/30' :
                        'bg-white/5 border-white/10'
                      }`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                inv.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                inv.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                inv.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                inv.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                'bg-white/20 text-white'
                              }`} data-testid={`invoice-status-${inv.id}`}>
                                {inv.status === 'pending' ? 'Pendiente' :
                                 inv.status === 'completed' ? 'Completada' :
                                 inv.status === 'rejected' ? 'Rechazada' :
                                 inv.status === 'failed' ? 'Fallida' : inv.status}
                              </span>
                              <span className="text-white text-sm font-medium truncate">{inv.customer_name}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
                              <span className="text-slate-400">RFC: <span className="text-white">{inv.receptor_rfc}</span></span>
                              <span className="text-slate-400">Total: <span className="text-white font-medium">${inv.total?.toLocaleString('es-MX', {minimumFractionDigits: 2})} {inv.moneda}</span></span>
                              <span className="text-slate-400">Razón Social: <span className="text-white">{inv.receptor_nombre}</span></span>
                              <span className="text-slate-400">Código: <span className="text-[#aa2aff]">{inv.recovery_code || 'N/A'}</span></span>
                            </div>
                            {inv.cfdi_uuid && (
                              <p className="text-green-400 text-xs mt-1">UUID: {inv.cfdi_uuid}</p>
                            )}
                            {inv.error_message && (
                              <p className="text-red-400 text-xs mt-1">Error: {inv.error_message}</p>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-1.5 flex-shrink-0">
                            {inv.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleApproveInvoice(inv.id)}
                                  className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 h-7"
                                  data-testid={`btn-approve-invoice-${inv.id}`}
                                >
                                  Aprobar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRejectInvoice(inv.id)}
                                  className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs px-3 py-1 h-7"
                                  data-testid={`btn-reject-invoice-${inv.id}`}
                                >
                                  Rechazar
                                </Button>
                              </>
                            )}
                            {inv.status === 'completed' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownloadInvoicePDF(inv.id)}
                                  className="border-white/20 text-white hover:bg-white/10 text-xs px-3 py-1 h-7"
                                  data-testid={`btn-download-pdf-${inv.id}`}
                                >
                                  <Download className="w-3 h-3 mr-1" /> PDF
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownloadInvoiceXML(inv.id)}
                                  className="border-white/20 text-white hover:bg-white/10 text-xs px-3 py-1 h-7"
                                  data-testid={`btn-download-xml-${inv.id}`}
                                >
                                  <Download className="w-3 h-3 mr-1" /> XML
                                </Button>
                              </>
                            )}
                            {inv.status === 'failed' && (
                              <Button
                                size="sm"
                                onClick={() => handleApproveInvoice(inv.id)}
                                className="bg-amber-600 hover:bg-amber-700 text-white text-xs px-3 py-1 h-7"
                              >
                                Reintentar
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="materials" className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-400">
                    <FileText className="w-4 h-4 inline mr-1" />
                    Materiales subidos por clientes
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadMaterials}
                    className="border-slate-600 text-slate-300 hover:bg-white/5"
                    data-testid="btn-load-materials"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Actualizar
                  </Button>
                </div>

                {isLoadingMaterials ? (
                  <div className="text-center py-8 text-slate-400">
                    Cargando materiales...
                  </div>
                ) : materials.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay materiales disponibles</p>
                    <p className="text-xs mt-1">Los materiales aparecerán cuando los clientes suban archivos</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadMaterials}
                      className="mt-4 border-[#aa2aff] text-[#aa2aff] hover:bg-[#aa2aff]/10"
                    >
                      Cargar Materiales
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                    {materials.map((item) => (
                      <div 
                        key={item.id} 
                        className="p-4 bg-[#080c2d]/50 rounded-lg border border-white/10"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="text-white font-medium">{item.book_title || item.customer_name || 'Sin título'}</h4>
                            <p className="text-xs text-slate-400">{item.author_name || item.customer_email}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {new Date(item.created_at).toLocaleDateString('es-MX', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          {item.cover_url && (
                            <>
                              <a
                                href={`${BACKEND_URL}${item.cover_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-xs hover:bg-blue-500/30 transition-colors"
                                data-testid={`download-cover-${item.id}`}
                              >
                                <Download className="w-3 h-3" />
                                Portada
                              </a>
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.delete(`${API}/materials`, { data: { quote_id: item.id, field: 'cover_url' } });
                                    toast.success('Portada eliminada');
                                    loadMaterials();
                                  } catch (e) { toast.error('Error al eliminar'); }
                                }}
                                className="flex items-center gap-1 px-2 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs hover:bg-red-500/30 transition-colors"
                                data-testid={`delete-cover-${item.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </>
                          )}
                          {item.fiscal_url && (
                            <>
                              <a
                                href={`${BACKEND_URL}${item.fiscal_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-xs hover:bg-green-500/30 transition-colors"
                                data-testid={`download-fiscal-${item.id}`}
                              >
                                <Download className="w-3 h-3" />
                                Constancia Fiscal
                              </a>
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.delete(`${API}/materials`, { data: { quote_id: item.id, field: 'fiscal_url' } });
                                    toast.success('Constancia fiscal eliminada');
                                    loadMaterials();
                                  } catch (e) { toast.error('Error al eliminar'); }
                                }}
                                className="flex items-center gap-1 px-2 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs hover:bg-red-500/30 transition-colors"
                                data-testid={`delete-fiscal-${item.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </>
                          )}
                          {item.book_pdf_url && (
                            <>
                              <a
                                href={`${BACKEND_URL}${item.book_pdf_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg text-xs hover:bg-purple-500/30 transition-colors"
                                data-testid={`download-pdf-${item.id}`}
                              >
                                <Download className="w-3 h-3" />
                                Libro PDF
                              </a>
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.delete(`${API}/materials`, { data: { quote_id: item.id, field: 'book_pdf_url' } });
                                    toast.success('PDF eliminado');
                                    loadMaterials();
                                  } catch (e) { toast.error('Error al eliminar'); }
                                }}
                                className="flex items-center gap-1 px-2 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs hover:bg-red-500/30 transition-colors"
                                data-testid={`delete-pdf-${item.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="history" className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-400">
                    <History className="w-4 h-4 inline mr-1" />
                    {quotes.length} cotizaciones guardadas
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportToCSV}
                    className="border-slate-600 text-slate-300 hover:bg-white/5"
                    data-testid="btn-export-csv"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Exportar CSV
                  </Button>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Buscar por título, autor, código... (* = todas)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-[#080c2d]/50 border-[#272b96] text-white pl-10 placeholder:text-slate-500 focus:border-[#aa2aff]"
                    data-testid="input-search-quotes"
                  />
                </div>

                {/* Quotes List */}
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                  {isLoadingQuotes ? (
                    <div className="text-center py-8 text-slate-400">
                      Cargando cotizaciones...
                    </div>
                  ) : filteredQuotes.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      {searchTerm ? 'No se encontraron resultados' : 'No hay cotizaciones guardadas'}
                    </div>
                  ) : (
                    filteredQuotes.map((quote) => (
                      <div
                        key={quote.id}
                        className="p-3 bg-[#080c2d]/50 rounded-lg border border-white/5 hover:border-[#aa2aff]/30 transition-colors cursor-pointer group"
                        onClick={() => {
                          if (onLoadQuote) {
                            onLoadQuote(quote);
                            onOpenChange(false);
                          }
                        }}
                        data-testid={`quote-row-${quote.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-grow">
                            <h4 className="font-semibold text-white text-sm group-hover:text-[#aa2aff] transition-colors flex items-center gap-2">
                              {quote.book_title || quote.customer_name || 'Sin título'}
                              {quote.status === 'paid' && (
                                <span className="inline-flex items-center gap-0.5 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded px-1.5 py-0.5 text-[10px] font-medium">
                                  <Check className="w-2.5 h-2.5" /> PAGADA
                                </span>
                              )}
                              {quote.referrer_code && (
                                <span
                                  className="inline-flex items-center gap-0.5 bg-[#aa2aff]/10 text-[#aa2aff] border border-[#aa2aff]/30 rounded px-1.5 py-0.5 text-[10px] font-medium"
                                  title={`Referido: ${quote.referrer_name || quote.referrer_code}`}
                                >
                                  Ref: {quote.referrer_name || quote.referrer_code}
                                </span>
                              )}
                              {quote.commission_sent_at && (
                                <span className="inline-flex items-center gap-0.5 bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded px-1.5 py-0.5 text-[10px] font-medium">
                                  Comisión enviada
                                </span>
                              )}
                            </h4>
                            <p className="text-xs text-slate-400">{quote.author_name || quote.customer_email || '-'}</p>
                            <div className="flex gap-3 mt-1 text-xs text-slate-500">
                              {quote.page_count > 0 && <span>{quote.page_count} págs</span>}
                              {quote.estimated_duration_hours > 0 && <span>{quote.estimated_duration_hours}h</span>}
                              {quote.podcast_package_id && <span className="text-blue-400">🎙️ Podcast</span>}
                              <span className="text-[#aa2aff] font-medium">
                                {formatPrice(quote.grand_total || (quote.final_price * quote.estimated_duration_hours) || 0)}
                              </span>
                            </div>
                            <p className="text-xs text-slate-600 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              Clic para cargar esta cotización
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-xs text-slate-500">
                              {new Date(quote.created_at).toLocaleDateString('es-MX')}
                            </span>
                            <div className="flex gap-1">
                              {quote.status !== 'paid' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkPaid(quote);
                                  }}
                                  className="h-6 w-6 p-0 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                  title="Marcar como pagada"
                                  data-testid={`btn-mark-paid-${quote.id}`}
                                >
                                  <CreditCard className="w-3 h-3" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteQuote(quote.id);
                                }}
                                className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                data-testid={`btn-delete-${quote.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SettingsModal;
